/**
 * 取后端错误响应里的 `message`（`04-api-contract.md` §1.4 的统一错误结构）。
 *
 * `@cuito/api-client` 的 `readErrorMeta` 只给 `status` 与业务码 `code`，
 * 而有些提示**必须**用后端原话：例如仓库删除失败是
 * 「仓库仍有 N 种库存」（`WAREHOUSE_NOT_EMPTY`）、库存出库失败是
 * 「可用量不足：当前可用 25 件，本次调整 -999 件」（`STOCK_RESERVED_CONFLICT`）——
 * 前端自己拼一句通用文案会把「有几种 / 剩几件」这个唯一有用的信息丢掉。
 *
 * 这里不 import axios：`axios` 不是本应用的直接依赖，从 `@cuito/api-client`
 * 借道会在依赖提升变化时静默断掉。只按已知的响应形状做一次结构化取值。
 */
export function readServerMessage(error: unknown): string | null {
  if (typeof error !== 'object' || error === null) return null;
  const response = (error as { response?: { data?: { message?: unknown } } }).response;
  const message = response?.data?.message;
  return typeof message === 'string' && message !== '' ? message : null;
}

/**
 * 取后端错误响应里的业务码 `code`（`04-api-contract.md` §1.4）。
 *
 * 与 `readServerMessage` 同样的理由不 import axios：`@cuito/api-client` 的 `readErrorMeta`
 * 只认**真正的 axios 错误**（内部是 `axios.isAxiosError`），而组件测试里造一个这样的错误
 * 需要直接依赖 `axios` —— 它不是本应用的直接依赖（本文件头注释已说明这一点）。
 * 这里按已知响应形状取 `code`，与页面上已有的 `readServerMessage` 走同一条判据。
 *
 * 用途：409 `ORDER_ALREADY_ACCEPTED`（抢单输家）与 400 `ORDER_GATE_NOT_SATISFIED`
 * （交付门）都必须靠**业务码**分辨，只看 HTTP 状态会把两种 409/400 混成一句话。
 */
export function readErrorCode(error: unknown): string | null {
  if (typeof error !== 'object' || error === null) return null;
  const code = (error as { response?: { data?: { code?: unknown } } }).response?.data?.code;
  return typeof code === 'string' && code !== '' ? code : null;
}

/**
 * 取后端错误响应里的 `details`（`04-api-contract.md` §1.4）。
 *
 * 与 `readServerMessage` 同样的理由不 import axios：只按已知形状做结构化取值。
 * 需要它的场景只有一个——`409 SHELF_OVERLAP` 的 `details.conflicts` 是
 * 「哪几个货架挡住了」的唯一信息，丢了它用户只能看到「重叠了」。
 */
export function readServerDetails(error: unknown): Record<string, unknown> | null {
  if (typeof error !== 'object' || error === null) return null;
  const response = (error as { response?: { data?: { details?: unknown } } }).response;
  const details = response?.data?.details;
  if (typeof details !== 'object' || details === null || Array.isArray(details)) return null;
  return details as Record<string, unknown>;
}
